import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChalkboardTeacher, Plus, MagnifyingGlass, Archive, ArrowCounterClockwise } from "@phosphor-icons/react";

const empty = { full_name: "", email: "", phone: "", subject: "", languages: [], bio: "", monthly_salary: 0, hourly_rate: 0, address: "", nationality: "", hire_date: "" };

export function TeacherForm({ initial, onSave, onClose }) {
  const { t } = useTranslation();
  const isEdit = !!initial?.id;
  const [f, setF] = useState(() => {
    if (!initial) return empty;
    return {
      ...empty,
      ...initial,
      languages: Array.isArray(initial.languages) ? initial.languages.join(", ") : (initial.languages || ""),
    };
  });
  const [saving, setSaving] = useState(false);
  const setV = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const submit = async () => {
    if (!f.full_name) return toast.error(t("students.toasts.fullNameRequired"));
    setSaving(true);
    try {
      const payload = { ...f, monthly_salary: Number(f.monthly_salary) || 0, hourly_rate: Number(f.hourly_rate) || 0,
        languages: typeof f.languages === "string" ? f.languages.split(",").map((s) => s.trim()).filter(Boolean) : f.languages };
      const { data } = isEdit
        ? await api.put(`/teachers/${initial.id}`, payload)
        : await api.post("/teachers", payload);
      toast.success(isEdit ? t("teachers.toasts.updated") : t("teachers.toasts.added"));
      onSave(data); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
      {[
        [t("teachers.form.fullName"), "full_name"], [t("teachers.form.email"), "email"], [t("teachers.form.phone"), "phone"],
        [t("teachers.form.subject"), "subject"], [t("teachers.form.languages"), "languages"],
        [t("teachers.form.monthlySalary"), "monthly_salary", "number"], [t("teachers.form.hourlyRate"), "hourly_rate", "number"],
        [t("teachers.form.address"), "address"], [t("teachers.form.nationality"), "nationality"],
        [t("teachers.form.hireDate"), "hire_date", "date"],
      ].map(([lbl, k, type]) => (
        <div key={k}>
          <Label className="text-xs uppercase tracking-wider font-semibold">{lbl}</Label>
          <Input type={type || "text"} value={f[k] ?? ""} onChange={setV(k)} className="mt-1.5 h-10"
            data-testid={`teacher-field-${k}`} />
        </div>
      ))}
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("teachers.form.bio")}</Label>
        <Textarea value={f.bio} onChange={setV("bio")} rows={3} className="mt-1.5" data-testid="teacher-field-bio" />
      </div>
      <DialogFooter className="md:col-span-2">
        <Button variant="outline" onClick={onClose} data-testid="teacher-form-cancel">{t("common.cancel")}</Button>
        <Button onClick={submit} disabled={saving} data-testid="teacher-form-save">
          {saving ? t("common.saving") : (isEdit ? t("common.saveChanges") : t("teachers.addTeacher"))}
        </Button>
      </DialogFooter>
    </div>
  );
}

export default function Teachers() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const canManage = ["super_admin", "director", "branch_manager"].includes(user?.role);

  const load = async () => {
    try {
      const { data } = await api.get("/teachers", { params: { archived: showArchived } });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [showArchived]);

  const archive = async (id, e) => {
    e.stopPropagation();
    try { await api.post(`/teachers/${id}/archive`); toast.success(t("teachers.toasts.archived")); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const restore = async (id, e) => {
    e.stopPropagation();
    try { await api.post(`/teachers/${id}/restore`); toast.success(t("teachers.toasts.restored")); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = rows.filter((r) => !q || r.full_name.toLowerCase().includes(q.toLowerCase()) || (r.subject || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{t("nav.teachers")}</div>
          <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: "Manrope" }}>{t("teachers.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("teachers.subtitle")}</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-teacher-btn" className="rounded-full gap-2"><Plus size={16} weight="bold" /> {t("teachers.addTeacher")}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle style={{ fontFamily: "Manrope" }}>{t("teachers.newTeacher")}</DialogTitle></DialogHeader>
              <TeacherForm onSave={load} onClose={() => setOpen(false)} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="teachers-search" placeholder={t("common.search")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-10 h-10" />
        </div>
        {canManage && (
          <button
            data-testid="show-archived-teachers"
            onClick={() => setShowArchived((v) => !v)}
            className={`h-10 px-4 rounded-full text-xs font-semibold border transition-colors flex items-center gap-2 ${showArchived ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
          >
            <Archive size={14} weight={showArchived ? "fill" : "regular"} />
            {showArchived ? t("common.hideArchived") : t("common.showArchived")}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((tch) => (
          <Card key={tch.id} data-testid={`teacher-card-${tch.id}`}
            className="border-border/60 card-hover cursor-pointer" onClick={() => nav(`/teachers/${tch.id}`)}>
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="w-14 h-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center text-xl font-bold shrink-0">
                  {tch.full_name?.charAt(0)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold" style={{ fontFamily: "Manrope" }}>{tch.full_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{tch.subject}</div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(tch.languages || []).slice(0, 3).map((l) => (
                      <Badge key={l} variant="secondary" className="text-[10px]">{l}</Badge>
                    ))}
                    {tch.archived && <Badge variant="destructive" className="text-[10px]">{t("common.archived")}</Badge>}
                  </div>
                </div>
              </div>
              {tch.monthly_salary > 0 && (
                <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("teachers.card.monthly")}</span>
                  <span className="font-mono font-bold text-sm">${tch.monthly_salary}</span>
                </div>
              )}
              {canManage && (
                <div className="mt-3 pt-3 border-t border-border flex justify-end">
                  {tch.archived ? (
                    <Button size="sm" variant="outline" onClick={(e) => restore(tch.id, e)}
                      data-testid={`restore-teacher-${tch.id}`} className="gap-1.5 h-8 text-xs">
                      <ArrowCounterClockwise size={12} weight="bold" /> {t("common.restore")}
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={(e) => archive(tch.id, e)}
                      data-testid={`archive-teacher-${tch.id}`} className="gap-1.5 h-8 text-xs text-muted-foreground hover:text-destructive">
                      <Archive size={12} weight="bold" /> {t("common.archive")}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
